import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';
import DeterminationKeyViewer from './DeterminationKeyViewer';
import DeterminationKeyFlatViewer from './DeterminationKeyFlatViewer';
// A static `import ... from 'ext:champis-species/...'` captures whatever
// flarum.reg holds at *this module's own* evaluation time, which isn't
// guaranteed to be after champis-species has finished registering its own
// exports (see champis-forum's DiscussionFilters.js, the other place this
// exact lazy-lookup pattern is used, for the confirmed failure that reusing
// a static import hit). Resolved lazily instead, at actual render time.
//
// Typed as `typeof Page` (any real Component subclass works as a JSX tag
// type - Page is already imported above) rather than Mithril.ComponentTypes:
// a bare ComponentTypes union has no construct/call signature TypeScript's
// own JSX checker recognizes, so `<SpeciesSearchWidget />` below fails to
// compile with it (confirmed directly - "does not have any construct or
// call signatures").
function getSpeciesSearchWidget() {
  return window.flarum.reg.get('champis-species', 'forum/components/SpeciesSearchWidget');
}

// champis-forum's startDiscussion(): the composer with this page's category
// preselected (or the log-in modal) - looked up at click time, same reason
// as getSpeciesSearchWidget() above.
function startDiscussion(tagSlug) {
  window.flarum.reg.get('champis-forum', 'forum/utils/startDiscussion')?.(tagSlug);
}
const PAGE_LIMIT = 50;
const VISIBLE_STEP = 30;
const CLES_TAG_SLUG = 'cles-de-determination';

// Same URL champis.net/keys itself links "Mycoclé" and "Mycoclé, G.
// Fannechère" to on every single row, verified directly against the live
// page's DOM (identical regardless of genus or viewable/coded status).
const MYCOCLE_URL = 'http://mycolim.free.fr/DOC_SML/mycoCLE/Charg_Mycocle.htm';

// Strips accents for the sort so it matches how a French speaker expects
// "Périgord" to sort next to "Pazmany", not after "Zzz" (same helper as
// champis-species' DescriptionsPage.js).
function normalize(text) {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/**
 * /keys - every discussion in the "Clés de détermination" forum that has a
 * determination-key file attached (see the `determinationKeyFile` field on
 * DiscussionResource, extend.php): title, download + (when parseable) view
 * buttons, and the same explanatory caption champis.net/keys shows under
 * each row (verbatim: readable-by-Mycoclé / coded-unreadable / PDF,
 * depending on file type and CleKeyParser's own viewable check - see
 * fileNote()), each linking "Mycoclé"/"Mycoclé, G. Fannechère" the same way
 * the reference page does.
 *
 * Same blue-header-bar + boxed-card chrome as /descriptions
 * (champis-species' DescriptionsPage.js), and the same row design language
 * established across the site's other lists this session (whole block is
 * one link, gray hover fill, no underline, 14px) - see forum.less. The row
 * is a real <a href download> (a real file download, not an internal
 * route, so a plain anchor rather than Link) with two Button-styled <span>s
 * inside it: download (primary, decorative - pointer-events: none, clicks
 * fall through to the row's own href) and, when viewable, voir (a real
 * nested control that opens DeterminationKeyViewer - see openViewer()).
 *
 * Still loads every discussion up front (same reasoning as
 * DescriptionsPage.js: this forum has ~180 discussions, comfortably small
 * to hold in memory at once) - filtering by title is instant either way,
 * and doing it client-side over the full set means the search box isn't
 * limited to whatever's currently visible. VISIBLE_STEP only controls how
 * many of the (possibly search-filtered) results are actually *rendered*
 * at once - "Charger plus" reveals more of what's already in memory, no
 * extra request.
 */
export default class DeterminationKeysPage extends Page {
  discussions = null;
  error = false;
  query = '';
  visibleCount = (() => VISIBLE_STEP)();
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(app.translator.trans('champis-keys.forum.keys_page.title'));
    this.discussions = null;
    this.error = false;
    this.query = '';
    this.visibleCount = VISIBLE_STEP;
    this.loadAll();
  }
  loadAll() {
    this.discussions = null;
    this.error = false;
    const url = app.forum.attribute('apiUrl') + '/discussions';
    // Only the fields row() renders: every other discussion field still gets
    // computed otherwise - champis-species' own `species` one included, a
    // live GBIF lookup (name + vernacular name) for each of the ~225 species
    // these keys are filed under, which made a cold-cache /keys take minutes.
    // Same reason Content\Keys (the server-rendered version) asks for them.
    // And of each first post, only whether it may be edited (its
    // "Modifier", see editButton()).
    const pageParams = offset => ({
      filter: {
        tag: CLES_TAG_SLUG
      },
      page: {
        limit: PAGE_LIMIT,
        offset
      },
      include: 'firstPost',
      fields: {
        discussions: 'title,determinationKeyFile,keyMetadata,firstPost',
        posts: 'canEdit,isHidden'
      }
    });

    // Each discussion's `determinationKeyFile` field opens and reads a few
    // bytes of its attached file on disk to decide whether to show a "Voir"
    // button (CleKeyParser::looksViewableFile() - a .zip needs a real
    // ZipArchive open to find its .cle entry first, pricier than the plain
    // .cle case), so ~180 discussions across several pages adds up - same
    // reasoning and fix as champis-species' DescriptionsPage.js: once the
    // first page reports the real total (`meta.page.total`), every
    // remaining page is requested *concurrently* instead of one after
    // another, and everything is sorted once at the end rather than
    // re-sorted (and visibly reshuffled on screen) after every page.
    app.request({
      method: 'GET',
      url,
      params: pageParams(0)
    }).then(firstPayload => {
      const total = firstPayload.meta?.page?.total ?? 0;
      const firstPage = app.store.pushPayload(firstPayload);
      const remainingOffsets = [];
      for (let offset = PAGE_LIMIT; offset < total; offset += PAGE_LIMIT) {
        remainingOffsets.push(offset);
      }
      Promise.all(remainingOffsets.map(offset => app.request({
        method: 'GET',
        url,
        params: pageParams(offset)
      }))).then(remainingPayloads => {
        const all = remainingPayloads.reduce((acc, payload) => acc.concat(app.store.pushPayload(payload)), firstPage);
        this.discussions = all.filter(d => d.determinationKeyFile()).sort((a, b) => normalize(a.title()).localeCompare(normalize(b.title())));
        m.redraw();
      }).catch(() => {
        this.error = true;
        m.redraw();
      });
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  filteredDiscussions() {
    if (!this.discussions) return [];
    const q = normalize(this.query.trim());
    if (!q) return this.discussions;
    return this.discussions.filter(d => normalize(d.title()).includes(q));
  }
  view() {
    const filtered = this.discussions ? this.filteredDiscussions() : [];
    const visible = filtered.slice(0, this.visibleCount);
    const SpeciesSearchWidget = getSpeciesSearchWidget();
    return m("div", {
      className: "Page DeterminationKeysPage"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "Page-container"
    }, m("div", {
      className: "Page-content"
    }, m("h1", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-key",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-keys.forum.keys_page.title')), this.discussions && this.discussions.length > 0 && m("div", {
      className: "IndexPage-toolbar"
    }, m("div", {
      className: "ChampisKeysList-search"
    }, m("i", {
      className: "icon fas fa-search",
      "aria-hidden": "true"
    }), m("input", {
      className: "FormControl",
      type: "search",
      placeholder: extractText(app.translator.trans('champis-keys.forum.keys_page.search_placeholder')),
      "aria-label": extractText(app.translator.trans('champis-keys.forum.keys_page.search_placeholder')),
      value: this.query,
      oninput: e => {
        this.query = e.target.value;
        this.visibleCount = VISIBLE_STEP;
      }
    }))), m("div", {
      className: "ChampisKeysList"
    }, !this.discussions && this.error ? m(Placeholder, {
      text: app.translator.trans('champis-keys.forum.keys_page.error')
    }) : !this.discussions ? m(LoadingIndicator, null) : filtered.length === 0 ? m(Placeholder, {
      text: app.translator.trans(this.discussions.length === 0 ? 'champis-keys.forum.keys_page.empty' : 'champis-keys.forum.keys_page.no_results')
    }) : m("ul", {
      className: "ChampisKeysList-items"
    }, visible.map(discussion => this.row(discussion)))), visible.length < filtered.length && m("div", {
      className: "ChampisKeysList-loadMore"
    }, m(Button, {
      className: "Button",
      onclick: () => this.visibleCount += VISIBLE_STEP
    }, app.translator.trans('champis-keys.forum.keys_page.load_more_button')))), m("div", {
      className: "Page-sidebar"
    }, SpeciesSearchWidget && m(SpeciesSearchWidget, null), this.newDiscussionButton()))));
  }

  // Same button (icon, classes, disabled/login-fallback logic, core's own
  // translator keys) as core's IndexSidebar.tsx renders on a tag page's own
  // sidebar - a plain copy rather than reusing IndexSidebar itself, which
  // would need a routeName ('index'/'tag') this page doesn't have to get
  // past champis-forum's own restrictSidebarWidgetsToIndexPages.js gate.
  newDiscussionButton() {
    const canStartDiscussion = app.forum.attribute('canStartDiscussion') || !app.session.user;
    return m(Button, {
      icon: "fas fa-edit",
      className: "Button Button--primary IndexPage-newDiscussion",
      "aria-label": app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`),
      onclick: () => startDiscussion(CLES_TAG_SLUG),
      disabled: !canStartDiscussion
    }, app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`));
  }
  row(discussion) {
    const file = discussion.determinationKeyFile();
    return m("li", {
      key: discussion.id()
    }, m("a", {
      href: file.url,
      download: file.fileName,
      className: "ChampisKeysList-item"
    }, m("span", {
      className: "ChampisKeysList-title"
    }, discussion.title()), this.metaLine(discussion.keyMetadata()), m("span", {
      className: "ChampisKeysList-actions"
    }, m("span", {
      className: "Button Button--primary ChampisKeysList-button"
    }, m("i", {
      className: "icon fas fa-download",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-keys.forum.keys_page.download_button')), file.viewable && m("span", {
      className: "Button ChampisKeysList-viewButton",
      role: "button",
      tabIndex: 0,
      onclick: e => this.openViewer(e, file),
      onkeydown: e => {
        if (e.key === 'Enter' || e.key === ' ') this.openViewer(e, file);
      }
    }, m("i", {
      className: "icon fas fa-eye",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-keys.forum.keys_page.view_button')), file.viewable && m("span", {
      className: "Button ChampisKeysList-viewButton",
      role: "button",
      tabIndex: 0,
      onclick: e => this.openFlatViewer(e, file),
      onkeydown: e => {
        if (e.key === 'Enter' || e.key === ' ') this.openFlatViewer(e, file);
      }
    }, m("i", {
      className: "icon fas fa-align-left",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-keys.forum.keys_page.view_flat_button')), this.editButton(discussion)), this.fileNote(file)));
  }

  // "Modifier", for whoever may edit the key's post (its file, its text):
  // the forum's edit composer, as on the other pages showing a discussion's
  // text (champis-forum's editPost, looked up at render time through
  // flarum.reg). A role="button" <span>, not champis-forum's EditPostButton
  // (a <button>): it sits inside the row's own download link, like "Voir".
  editButton(discussion) {
    const post = discussion.firstPost();
    const editPost = window.flarum.reg.get('champis-forum', 'forum/utils/editPost');
    if (!post || !editPost || !post.canEdit() || post.isHidden()) return null;
    const edit = e => {
      e.preventDefault();
      e.stopPropagation();
      editPost(post);
    };
    return m("span", {
      className: "Button Button--link ChampisKeysList-editButton",
      role: "button",
      tabIndex: 0,
      onclick: edit,
      onkeydown: e => {
        if (e.key === 'Enter' || e.key === ' ') edit(e);
      }
    }, m("i", {
      className: "icon fas fa-pencil-alt",
      "aria-hidden": "true"
    }), " ", app.translator.trans('core.forum.post_controls.edit_button'));
  }

  // Author and/or language (see champis-keys' `keyMetadata` field on
  // DiscussionResource, extend.php) - a small subtitle right under the
  // title, same "primary · secondary" join already used elsewhere on this
  // site (e.g. SpeciesGbifSearchField's name/status row). Renders nothing
  // when a key has neither set, which most historical rows still don't
  // (see migration/14_key_metadata.php).
  metaLine(metadata) {
    if (!metadata || !metadata.author && !metadata.language) return null;
    return m("span", {
      className: "ChampisKeysList-meta"
    }, metadata.author, metadata.author && metadata.language && ' · ', metadata.language);
  }

  // Matches champis.net/keys' own captions under each row, verbatim -
  // .cle/.zip (Mycoclé's own format) get one of two notes depending on
  // whether CleKeyParser could actually make sense of it (see its own
  // docblock for the two file-format eras), PDFs get a plain format note,
  // anything else (there's currently nothing else on this site) gets none.
  //
  // "Mycoclé"/"Mycoclé, G. Fannechère" are real external links, same as on
  // the reference page. Flarum's translator only accepts *tag*-style rich
  // parameters (`<mycocle>Mycoclé</mycocle>` in the locale string, an empty
  // `<a>` here supplying just the attrs - confirmed against core's own
  // usage, e.g. EditCustomCssModal's `{ a: <a href="..." /> }` for its own
  // `<a>default styles</a>` string) - a plain `{mycocle}` placeholder
  // fed a vnode does NOT render as rich content, it was tried and reverted.
  // stopPropagation() keeps a click from also triggering the row's own
  // download (these are nested inside that <a>, see row() - same reasoning
  // as openViewer()'s handling of the "Voir" button, just without
  // preventDefault() since these *should* navigate, just not also trigger
  // the parent's).
  fileNote(file) {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-keys.forum.keys_page.${key}`, params);
    };
    const mycocleLinkAttrs = () => m("a", {
      href: MYCOCLE_URL,
      target: "_blank",
      rel: "noopener noreferrer",
      onclick: e => e.stopPropagation()
    });
    if (/\.pdf$/i.test(file.fileName)) {
      return m("div", {
        className: "ChampisKeysList-note"
      }, t('pdf_note'));
    }
    if (/\.(cle|zip)$/i.test(file.fileName)) {
      return m("div", {
        className: "ChampisKeysList-note"
      }, m("div", null, t(file.viewable ? 'cle_readable_note' : 'cle_coded_note', {
        mycocle: mycocleLinkAttrs()
      })), m("div", null, t('cle_source', {
        mycoclecredit: mycocleLinkAttrs()
      })));
    }
    return null;
  }

  // The row itself is a real <a href download> (the primary action), so
  // opening the viewer from the nested "Voir" button needs to stop that
  // click from ALSO triggering the parent link's navigation - same
  // nested-anchor handling used elsewhere in this codebase (e.g.
  // champis-species' addSpeciesLabels.js).
  openViewer(e, file) {
    e.preventDefault();
    e.stopPropagation();
    app.modal.show(DeterminationKeyViewer, {
      uuid: file.uuid,
      fileName: file.fileName
    });
  }
  openFlatViewer(e, file) {
    e.preventDefault();
    e.stopPropagation();
    app.modal.show(DeterminationKeyFlatViewer, {
      uuid: file.uuid,
      fileName: file.fileName
    });
  }
}
flarum.reg.add('champis-keys', 'forum/components/DeterminationKeysPage', DeterminationKeysPage);